import * as React from 'react'
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { boldRegexMatch, buildRegexExpession } from "../../Commons/Helpers";
import { useContent } from '../../Content/cms';
import { useFeatureToogle } from '../../ProtectedRoutes/FeatureToggle/hook';

// Import store
import {StoreState} from "../../store";

// Import interface
import {Participant} from "../interfaces";
import {Row as DataFrameRow} from '../../Commons/Components/DataFrame/types';

// Import components
import DataFrame from "../../Commons/Components/DataFrame";
import {
    faArchive,
    faFilter,
    faTimes,
    faTrashRestore,
    faEye,       // Added for visibility toggle
    faEyeSlash   // Added for visibility toggle
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Link from "../../Commons/SiteMap/Link";
import Surveys from '../Surveys/Form';
import ProtocolLink from '../Protocol/protocolLink';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';
import InputGroup from 'react-bootstrap/InputGroup';
import FormControl from 'react-bootstrap/FormControl';
import Spinner from 'react-bootstrap/Spinner';
import Badge  from 'react-bootstrap/Badge';
import { ARCHIVE_PARTICIPANT, RESTORE_PARTICIPANT } from '../../store/participants/types';
import Apirest from '../../Content/Apirest';
import { IconButton } from '../../Commons/MultipleComponents';


interface FilteredParticipant {
    participant: Participant
    match: React.ReactNode
    matchedName: boolean // Added to track if the regex matched the name or the code
}

const ParticipantsList : React.FC = () => {

    let getContent = useContent();

    let {featureEnabled} = useFeatureToogle();

    const mobiles = require('is-mobile');
    let mobile = mobiles();
    
    // participant lists states
    let [participantsDataFrame, setParticipantsDataFrame] = useState<DataFrameRow[]>([]);
   
    // Filter participant states
    let [filterQuery, setFilterQuery] = useState<string>('');
    let [filteringParticipants, setFilteringParticipants] = useState<boolean>(false);
    let [filteredParticipants, setFilteredParticipants] = useState<DataFrameRow[]>([]);

    // Visibility toggle state: 0 = Masked (Default), 1 = First Visible, 2 = All Visible
    const [visibilityMode, setVisibilityMode] = useState<number>(0);

    let participants = useSelector<StoreState, Participant[]>( 
        state => state.participants,
    );

    let filterTaskID = useRef<number|undefined>();
    let inputFilterRef = useRef<any>();

    const dispatch = useDispatch();
    
    const archiveParticipantInStore = (participant_id: number) => dispatch({
        type: ARCHIVE_PARTICIPANT,
        payload: participant_id
    });

    const restoreParticipantInStore = (participant_id: number) => dispatch({
            type: RESTORE_PARTICIPANT,
            payload: participant_id
        });


    // Re-generate dataframe when participants, mobile layout, or visibilityMode changes
    useEffect(
        () => {
            setParticipantsDataFrame(Participant2Dataframe(participants.filter(p => p.active)));
        },
        [participants, mobiles, visibilityMode] // Added visibilityMode as dependency
    );

    // Re-run filter automatically if visibilityMode changes while a search is active
    useEffect(() => {
        if (filterQuery !== '') {
            filterParticipants(filterQuery);
        }
    }, [visibilityMode]); // eslint-disable-line react-hooks/exhaustive-deps


    const filterParticipants = (filter: string) => {
        if (filter === '') {
            setFilteredParticipants([]);
            setFilteringParticipants(false);
            return;
        }
    
        let regex = buildRegexExpession(encodeURI(filter).replace(' ',''));
        let newFilteredParticipants = [];
        let index = 0; // Track index for the first visible element logic
    
        for (let participant of participants) {
            // Define values to match: [0] is Code, [1] and [2] are Names
            let values = [
                participant.code || '', 
                (participant.first_name || '') + ' ' + (participant.last_name || ''),
                (participant.last_name || '') + ' ' + (participant.first_name || '')
            ];
    
            for (let i = 0; i < values.length; i++) {
                let value = values[i];
                let [matches, formattedMatch] = boldRegexMatch(regex, value);
                
                if (matches) {
                    newFilteredParticipants.push(
                        FilteredParticipant2Dataframe({
                            participant: participant,
                            match: formattedMatch,
                            matchedName: i > 0 // If i > 0, the match was on the name, not the code
                        }, index) // Pass index here
                    );
                    index++;
                    break;
                }
            }
        }
    
        setFilteredParticipants(newFilteredParticipants);
        setFilteringParticipants(false);
    };
    
    const [filteredParticipantHeader, participantsHeader] = participantHeaders()

    const archiveParticipant = (participant_id: number) => {
        archiveParticipantInStore(participant_id);
        Apirest.archive_participant(participant_id.toString());
        setFilterQuery('')
    }

    function restoreParticipant(participant_id: number) {
        restoreParticipantInStore(participant_id);
        Apirest.restore_participant(participant_id.toString());
        setFilterQuery('')
    }

    const columnswidth = [26,30,8,15,8];
    const filteredColumnsWidth = [26,30,8,15,8]
    const columToggle = [
        false,
        mobile,
        !featureEnabled(['archiveParticipant']),
        !featureEnabled(['recordProtocol']),
        !featureEnabled(['recordSurvey'])
    ];

    // Added index parameter to determine if it's the first row
    function Participant2Row(participant: Participant, index: number): DataFrameRow {
        // Determine visibility based on current mode
        const isVisible = visibilityMode === 2 || (visibilityMode === 1 && index === 0);
        const fullName = `${participant.last_name} ${participant.first_name}`;
        
        // CSS properties for blurring and preventing selection/copying of hidden text
        const maskStyle: React.CSSProperties = isVisible 
            ? { transition: 'filter 0.3s ease' } 
            : { 
                filter: 'blur(5px)', 
                userSelect: 'none', 
                opacity: 0.8,
                transition: 'filter 0.3s ease' 
            };

        return [
            <Link to={'participant'} params={[participant.id]}>
                {!mobile && participant.code}
                {mobile && <>
                    <p className={'p-0 m-0'}>{participant.code}</p>
                    {/* Apply mask to mobile subtitle as well */}
                    <small className={'text-muted'} style={maskStyle}>{fullName}</small>
                </>}
            </Link>,

            <>
                <Link to={'participant'} params={[participant.id]}>
                    <span style={maskStyle}>{fullName}</span>
                    {!participant.active && <small className='mx-2'>
                        <Badge variant='secondary'>{getContent('home__filtered_participant_list__archive_participant__badge')}</Badge>
                        </small>}
                    {participant.tags && participant.tags.length > 0 && participant.tags.map((t, i) => (
                        <Badge key={i.toString()} className={'ml-1 badge-tags'} style={maskStyle}>{t}</Badge>
                    ))}
                </Link>
            </>,

           <div className="text-center">
                <IconButton
                    onClick={!participant.active ? 
                        () => restoreParticipant(participant.id) 
                        :() => archiveParticipant(participant.id)}
                    icon={{ icon: !participant.active ? faTrashRestore :faArchive, color: "#5CB6E7" }}
                    text={!participant.active ? getContent('archived__participant__restore__tooltip') : getContent('home__participants_list__archive__tooltip')}
                />
            </div>,
            
            !participant.active ? null :
            <ProtocolLink 
                participant_id={participant.id.toString()} 
                align={'text-center'}
            />,
            
            !participant.active ? null :
            <Surveys participant_id={participant.id.toString()} onlyIcon align={'text-center'}/>
        ]
    }

    function participantHeaders() {
        
        // Cycle between 0, 1, 2
        const handleVisibilityToggle = () => {
            setVisibilityMode(prev => (prev + 1) % 3);
        };

        // Determine icon and color based on current mode
        const getVisibilityIcon = () => visibilityMode === 0 ? faEyeSlash : faEye;
        const getVisibilityColor = () => {
            if (visibilityMode === 0) return '#6c757d'; // Gray for masked
            if (visibilityMode === 1) return '#ffc107'; // Yellow/Warning for partially visible
            return '#28a745';                           // Green/Success for all visible
        };

        const toggleButton = (
            <button 
                onClick={handleVisibilityToggle}
                className="btn btn-link p-0 ml-2 border-0 bg-transparent"
                title="Toggle Name Visibility"
                style={{ outline: 'none', boxShadow: 'none' }}
            >
                <FontAwesomeIcon icon={getVisibilityIcon()} color={getVisibilityColor()} />
            </button>
        );

        // Wrapping header text and toggle button together
        const headerWithToggle = (
            <div className={'mb-2 d-flex align-items-center'}>
                <span className="mb-0">{getContent('home__participants_lists__participant_list__header__full_name')}</span>
                {toggleButton}
            </div>
        );

        const filteredParticipantsHeader = [
            <p className={'mb-2'}>{getContent('home__participants_lists__participant_list__header__match')}</p>,
            headerWithToggle,
            <p className={'w-100 text-center mb-2'}>{getContent('home__participants_lists__participant_list__header__archive')}</p>,
            <p className={'w-100 text-center mb-2'}>{getContent('home__participants_lists__participant_list__header__record')}</p>,
            <p className={'w-100 text-center mb-2'}>{getContent('home__participants_lists__participant_list__header__survey')}</p>
        ];

        const participantsHeader = [
            <p className={'mb-2'}>{getContent('home__participants_lists__participant_list__header__code')}</p>,
            headerWithToggle,
            <p className={'w-100 text-center mb-2'}>{getContent('home__participants_lists__participant_list__header__archive')}</p>,
            <p className={'w-100 text-center mb-2'}>{getContent('home__participants_lists__participant_list__header__record')}</p>,
            <p className={'w-100 text-center mb-2'}>{getContent('home__participants_lists__participant_list__header__survey')}</p>
        ];

        return [filteredParticipantsHeader, participantsHeader];
    }

    const delayedFilterParticipants = (filterQuery: string) => {
        setFilterQuery(filterQuery);
        clearTimeout(filterTaskID.current);
        filterTaskID.current = (setTimeout(
            () => {
                setFilteringParticipants(true);
                setTimeout(() => filterParticipants(filterQuery),0);
            },
            25
        ) as unknown) as number;
    };

    const onClear = () => {
        setFilterQuery('');
        (inputFilterRef as any).current.value ='';
    };

    // Passed index from loop to handle first-element logic
    const FilteredParticipant2Dataframe = (participant: FilteredParticipant, index: number) : DataFrameRow => {
        let row = Participant2Row(participant.participant, index);
        
        const isVisible = visibilityMode === 2 || (visibilityMode === 1 && index === 0);
        
        // If the regex matched the name, we must blur it in the match column too, unless it's visible.
        // If it matched the code, we leave it clear.
        const matchStyle: React.CSSProperties = (isVisible || !participant.matchedName)
            ? { transition: 'filter 0.3s ease' }
            : { filter: 'blur(5px)', userSelect: 'none', opacity: 0.8, transition: 'filter 0.3s ease' };

        row[0] = (
            <Link to={'participant'} params={[participant.participant.id]} >
                <span style={matchStyle}>{participant.match}</span>
            </Link>
        );
        return row;
    };

    // Added map argument index to be passed down
    const Participant2Dataframe = (participants: Participant[]) : DataFrameRow[] => {
        return participants
            .sort((p1,p2) => p1.created_at < p2.created_at  ? 1 : -1)
            .map((participant, index) => Participant2Row(participant, index));
    }

    if (participants.length === 0){
        return (
            <div className={"viewParticipant"}>
                <h5 className={'mt-4 p-2 text-primary '}>
                    {getContent("home__participants_lists__all_participants__title")}
                </h5>
                <Alert variant={'warning mt-3'}>
                    {getContent("home__participants_lists__alert_empty_list")}
                </Alert>
            </div>
        );
    }

    return (
        <Container className={'mt-3 '}>

            {/** Participant Filter form */}
            {participantsDataFrame.length > 0 && <Row className={'d-flex justify-content-end '}>
                <Col xs={12} md={5} className={'search-div'}>
                    <InputGroup className={"mb-2"}>
                        <InputGroup.Prepend >
                            <InputGroup.Text className={'bg-white'}>
                                {filteringParticipants ? <Spinner animation={'border'} size={'sm'}/> : <FontAwesomeIcon icon={faFilter}/>}
                            </InputGroup.Text>
                        </InputGroup.Prepend>
                        <FormControl
                            id={"inlineFormInputGroup"}
                            ref={inputFilterRef}
                            placeholder={getContent("home__participants_lists__filtered_participants__placeholder")}
                            onChange = {(event: any) => delayedFilterParticipants(event.target.value)}
                        />
                        <InputGroup.Prepend  className={'filter-search-reset'}>
                            <button onClick={onClear} className={'bg-white no-border'}> <FontAwesomeIcon icon={faTimes} color={ filterQuery === '' ? '#fff' : '#495057'}/> </button>
                        </InputGroup.Prepend>
                    </InputGroup>
                </Col>
            </Row>}

            {/** Participant list when THERE IS filter applied */}
            {filterQuery !== '' && <Row className={'viewParticipant'}>
                <Col>
                    <h5 className={'p-2 text-primary'}>
                        {getContent("home__participants_lists__filtered_participants__title")}
                    </h5>
                    <DataFrame
                        header={filteredParticipantHeader}
                        className={'header-left header-bordered'}
                        columnWidths={filteredColumnsWidth}
                        hiddenColumns={columToggle}
                        dataframe={filteredParticipants }
                        sortableColumns={[0,1]}
                        columnClassName={'text-center'}
                        showAllOption={false}
                    />
                </Col>
                
            </Row>}
            {/** Participant list when THERE IS NO filter applied */}
            {filterQuery === '' && <Row className={'viewParticipant'}>
                <Col>
                    <h5 className={'p-2 text-primary'}>
                        {getContent("home__participants_lists__all_participants__title")}
                    </h5>
                    {participantsDataFrame.length > 0 && <DataFrame
                        header={participantsHeader}
                        className={'header-left header-bordered'}
                        columnWidths={columnswidth}
                        hiddenColumns={columToggle}
                        dataframe={participantsDataFrame}
                        sortableColumns={[0,1]}
                        pageSize={20}
                        showAllOption={true}
                    />}
                </Col>          
            </Row>}
        </Container>
    );
};

export default ParticipantsList;